/*
  GP directory page — add / edit / delete GPs used as letter recipients.
  Seeded from the therapist's uploaded mailing list on first boot.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Pencil, Trash2, Star, ArrowLeft, Search, Users2, Sparkles, Loader2, ExternalLink, Check,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const EMPTY = {
  title: "Dr", first_name: "", last_name: "", clinic: "",
  address_line_1: "", address_line_2: "", suburb: "", state: "VIC",
  postcode: "", phone: "", fax: "", email: "", provider_number: "",
  notes: "", is_priority: false, is_favourite: false,
};

export default function GpDirectory() {
  const navigate = useNavigate();
  const [gps, setGps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // gp object or "new"

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/letters/gps");
      setGps(r.data.gps || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    if (!q.trim()) return gps;
    const t = q.trim().toLowerCase();
    return gps.filter((g) => {
      return [
        g.first_name, g.last_name, g.clinic, g.suburb, g.postcode, g.email,
      ].some((v) => (v || "").toLowerCase().includes(t));
    });
  }, [gps, q]);

  async function save(gp) {
    try {
      if (gp.id) {
        await api.put(`/letters/gps/${gp.id}`, gp);
      } else {
        await api.post("/letters/gps", gp);
      }
      toast.success("Saved");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function del(gp) {
    if (!window.confirm(`Remove ${gp.title} ${gp.first_name} ${gp.last_name}?`)) return;
    try {
      await api.delete(`/letters/gps/${gp.id}`);
      toast.success("Removed");
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function toggleFav(gp) {
    try {
      await api.put(`/letters/gps/${gp.id}`, { ...gp, is_favourite: !gp.is_favourite });
      refresh();
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 sm:px-8 md:px-10 py-8">
      <button
        onClick={() => navigate("/letters")}
        className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-950"
        data-testid="gps-back-btn"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to letters
      </button>
      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
            <Users2 className="h-3.5 w-3.5" /> Referring practices
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            GP directory
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
            Your saved GP contacts, used as the quick recipient picker on
            the letter composer. Add or edit here — changes flow straight
            into every future letter.
          </p>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY })}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="gp-add-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New GP
        </Button>
      </div>

      <Card className="mt-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="p-4 border-b border-zinc-100">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, clinic, suburb…"
              className="pl-8 border-zinc-300"
              data-testid="gp-search"
            />
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No GPs match.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Doctor</TableHead>
                <TableHead>Clinic</TableHead>
                <TableHead>Suburb</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((g) => (
                <TableRow key={g.id} data-testid={`gp-row-${g.id}`}>
                  <TableCell>
                    <button
                      onClick={() => toggleFav(g)}
                      data-testid={`gp-fav-${g.id}`}
                      title={g.is_favourite ? "Remove from favourites" : "Add to favourites"}
                    >
                      <Star
                        className={cn(
                          "h-4 w-4",
                          g.is_favourite ? "fill-amber-400 text-amber-500" : "text-zinc-300",
                        )}
                      />
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-zinc-950 text-sm">
                      {g.title} {g.first_name} {g.last_name}
                    </div>
                    {g.provider_number && (
                      <div className="text-2xs text-zinc-500 font-mono-num">{g.provider_number}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-zinc-700">{g.clinic}</TableCell>
                  <TableCell className="text-xs text-zinc-700">
                    {g.suburb}
                    {g.postcode ? <span className="text-zinc-400"> · {g.postcode}</span> : null}
                  </TableCell>
                  <TableCell className="text-2xs text-zinc-600">
                    {g.email && <div>{g.email}</div>}
                    {g.phone && <div className="font-mono-num">{g.phone}</div>}
                    {!g.email && !g.phone && <span className="text-zinc-400">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost" size="sm" className="h-7"
                      onClick={() => setEditing(g)}
                      data-testid={`gp-edit-${g.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="h-7 text-red-700 hover:text-red-800 hover:bg-red-50"
                      onClick={() => del(g)}
                      data-testid={`gp-del-${g.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {editing && (
        <GpDialog
          gp={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function GpDialog({ gp, onClose, onSave }) {
  const [form, setForm] = useState(gp);
  const [lookup, setLookup] = useState({ loading: false, result: null, error: null });
  const isNew = !gp.id;
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canLookup = !isNew && !!form.first_name && !!form.last_name;

  async function runLookup() {
    if (!canLookup) return;
    setLookup({ loading: true, result: null, error: null });
    try {
      const r = await api.post(`/letters/gps/${gp.id}/lookup-provider-number`);
      setLookup({ loading: false, result: r.data, error: null });
    } catch (e) {
      setLookup({
        loading: false, result: null,
        error: e?.response?.data?.detail || e.message || "Lookup failed",
      });
    }
  }

  function applySuggestion() {
    if (!lookup.result?.provider_number) return;
    setForm((f) => ({ ...f, provider_number: lookup.result.provider_number }));
    toast.success("Provider number applied — click Save to keep it");
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="gp-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add GP" : "Edit GP"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title" v={form.title} onChange={change("title")} testId="gp-title" />
          <div />
          <Field label="First name*" v={form.first_name} onChange={change("first_name")} testId="gp-first" />
          <Field label="Last name*" v={form.last_name} onChange={change("last_name")} testId="gp-last" />
          <Field label="Clinic" v={form.clinic} onChange={change("clinic")} testId="gp-clinic" colSpan={2} />
          <Field label="Address line 1" v={form.address_line_1} onChange={change("address_line_1")} testId="gp-a1" colSpan={2} />
          <Field label="Address line 2" v={form.address_line_2} onChange={change("address_line_2")} testId="gp-a2" colSpan={2} />
          <Field label="Suburb" v={form.suburb} onChange={change("suburb")} testId="gp-suburb" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">State</Label>
              <select
                value={form.state || ""}
                onChange={change("state")}
                className="w-full h-9 border border-zinc-300 rounded-md px-2 text-sm bg-white mt-1"
                data-testid="gp-state"
              >
                {["VIC","NSW","QLD","WA","SA","TAS","ACT","NT"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <Field label="Postcode" v={form.postcode} onChange={change("postcode")} testId="gp-postcode" />
          </div>
          <Field label="Phone" v={form.phone} onChange={change("phone")} testId="gp-phone" />
          <Field label="Fax" v={form.fax} onChange={change("fax")} testId="gp-fax" />
          <Field label="Email (for sending letters)" v={form.email} onChange={change("email")} testId="gp-email" colSpan={2} />
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
              Provider number
              {!isNew && (
                <button
                  type="button"
                  onClick={runLookup}
                  disabled={lookup.loading || !canLookup}
                  className="text-2xs normal-case tracking-normal font-medium text-indigo-700 hover:text-indigo-900 disabled:text-zinc-400 inline-flex items-center gap-1"
                  data-testid="gp-lookup-btn"
                  title="Ask the configured AI to search the web for this GP's Medicare Provider Number"
                >
                  {lookup.loading
                    ? <><Loader2 className="h-3 w-3 animate-spin" /> Searching…</>
                    : <><Sparkles className="h-3 w-3" /> AI lookup</>}
                </button>
              )}
            </Label>
            <Input
              value={form.provider_number || ""}
              onChange={change("provider_number")}
              className="border-zinc-300 mt-1"
              data-testid="gp-provnum"
            />
          </div>
          <Field label="Notes" v={form.notes} onChange={change("notes")} testId="gp-notes" colSpan={2} />
        </div>

        {(lookup.result || lookup.error) && (
          <div
            className="mt-2 p-3 border rounded-md bg-indigo-50/40 border-indigo-200 text-sm"
            data-testid="gp-lookup-result"
          >
            {lookup.error && (
              <div className="text-rose-800">{lookup.error}</div>
            )}
            {lookup.result && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-2xs uppercase tracking-wider text-zinc-500">AI suggests</span>
                  {lookup.result.provider_number ? (
                    <>
                      <code className="px-2 py-0.5 rounded bg-white border border-indigo-200 font-mono-num text-indigo-900">
                        {lookup.result.provider_number}
                      </code>
                      <span className={cn(
                        "text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded",
                        lookup.result.confidence === "high" && "bg-emerald-100 text-emerald-800",
                        lookup.result.confidence === "medium" && "bg-amber-100 text-amber-800",
                        lookup.result.confidence === "low" && "bg-zinc-200 text-zinc-700",
                      )}>
                        {lookup.result.confidence} confidence
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        onClick={applySuggestion}
                        className="ml-auto h-7 bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="gp-lookup-apply"
                      >
                        <Check className="h-3.5 w-3.5 mr-1" /> Apply
                      </Button>
                    </>
                  ) : (
                    <span className="text-zinc-700 italic">No verifiable provider number found</span>
                  )}
                </div>
                {lookup.result.notes && (
                  <div className="text-[0.75rem] text-zinc-700 mt-1.5">{lookup.result.notes}</div>
                )}
                {lookup.result.sources?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">Sources</div>
                    <ul className="space-y-0.5">
                      {lookup.result.sources.map((s, i) => (
                        <li key={i}>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[0.75rem] text-indigo-800 hover:underline inline-flex items-center gap-1"
                          >
                            {s.title || s.url} <ExternalLink className="h-3 w-3" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(form)}
            disabled={!form.first_name || !form.last_name}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="gp-save-btn"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, onChange, testId, colSpan = 1 }) {
  return (
    <div className={cn(colSpan === 2 && "sm:col-span-2")}>
      <Label className="text-2xs uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input value={v || ""} onChange={onChange} className="border-zinc-300 mt-1" data-testid={testId} />
    </div>
  );
}
